<script setup lang="ts">
import { AlertTriangle, X } from 'lucide-vue-next';
import { computed } from 'vue';

export interface ConfirmModalProps {
  cancelText?: string;
  confirmText?: string;
  loading?: boolean;
  message: string;
  modelValue: boolean;
  title?: string;
  type?: 'danger' | 'info' | 'success' | 'warning';
}

const props = withDefaults(defineProps<ConfirmModalProps>(), {
  cancelText: 'Cancelar',
  confirmText: 'Confirmar',
  loading: false,
  title: 'Confirmar ação',
  type: 'danger',
});

const emit = defineEmits<{
  'cancel': [];
  'confirm': [];
  'update:modelValue': [value: boolean];
}>();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const confirmButtonClass = computed(() => {
  const classMap = {
    danger: 'btn-danger',
    info: 'btn-info',
    success: 'btn-success',
    warning: 'btn-warning',
  };
  return `btn ${classMap[props.type]}`;
});

const iconComponent = computed(() => AlertTriangle);

const handleConfirm = () => {
  emit('confirm');
};

const handleCancel = () => {
  emit('cancel');
  isVisible.value = false;
};

const handleClose = () => {
  if (!props.loading) {
    handleCancel();
  }
};
</script>

<template>
  <div
    v-if="isVisible"
    class="modal modal-blur fade show"
    style="display: block;"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @keydown.escape="handleClose"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm" role="document">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">
            <component :is="iconComponent" :size="20" class="me-2 text-muted" />
            {{ title }}
          </h5>
          <button
            v-if="!loading"
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="handleClose"
          >
            <X :size="20" />
          </button>
        </div>

        <div class="modal-body">
          <p class="mb-0">{{ message }}</p>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="loading"
            @click="handleCancel"
          >
            {{ cancelText }}
          </button>

          <button
            type="button"
            :class="confirmButtonClass"
            :disabled="loading"
            @click="handleConfirm"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            {{ loading ? 'Processando...' : confirmText }}
          </button>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="modal-backdrop fade show"
      @click="handleClose"
    />
  </div>
</template>

<style scoped lang="scss">
.modal {
  z-index: 1055;

  &.show {
    display: block !important;
  }

  .modal-dialog {
    z-index: 1060;
    position: relative;
  }
}

.modal-backdrop {
  z-index: 1050;
}

.modal-header {
  background-color: var(--bs-light);
  border-bottom: 1px solid var(--bs-border-color);

  .modal-title {
    font-weight: 600;
    color: var(--bs-dark);
    display: flex;
    align-items: center;
  }
}

.btn-close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--bs-secondary);
  transition: color 0.15s ease-in-out;

  &:hover {
    color: var(--bs-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.modal-footer {
  background-color: var(--bs-light);
  border-top: 1px solid var(--bs-border-color);

  .btn {
    min-width: 100px;
  }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
